/**
 * SendMessageDialog
 *
 * Simple dialog for dispatchers to send a direct message to a guard.
 * Calls POST /api3.2/dispatch/messages which triggers a push notification
 * to the guard's mobile device(s).
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { sendMessageToGuard } from '../../../../../api/dispatch/dispatchApi';

interface SendMessageDialogProps {
  open: boolean;
  onClose: () => void;
  guardId: number;
  guardName: string;
}

const SendMessageDialog: React.FC<SendMessageDialogProps> = ({
  open,
  onClose,
  guardId,
  guardName,
}) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSend = async () => {
    if (!message.trim()) return;

    setIsSending(true);
    setError(null);

    try {
      await sendMessageToGuard({
        toContactId: guardId,
        message: message.trim(),
      });
      setSuccess(true);
      setMessage('');
      // Auto-close after brief success state
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    if (isSending) return;
    setMessage('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Send Message to {guardName}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          The guard will receive a push notification on their mobile device.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Message sent successfully
          </Alert>
        )}

        <TextField
          autoFocus
          multiline
          rows={3}
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
          disabled={isSending || success}
          helperText={`${message.length}/1000`}
          inputProps={{ maxLength: 1000 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSending}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={!message.trim() || isSending || success}
          startIcon={isSending ? <CircularProgress size={16} /> : undefined}
        >
          {isSending ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendMessageDialog;
